/**
 * What the group's balances will read once an import has run.
 */
import { roundToCurrencyPrecision } from '@splitfiesta/shared';

import type { ImportReconciliation } from './importConversion';

/**
 * Net per member column, in the group currency, of the rows that will import
 * — the sum of the very vectors the server will store, so the preview cannot
 * drift from the import. Positive is owed, negative owes.
 *
 * `null`, never a partial sum, while a row is still in its own currency for
 * want of a rate: a balance short of some of its rows would read as a real
 * figure rather than as a gap.
 */
export function balancesAfterImport(
  reconciliation: ImportReconciliation,
  groupCurrency: string,
  memberCount: number
): number[] | null {
  const group = groupCurrency.toUpperCase();
  const totals = new Array<number>(Math.max(memberCount, 0)).fill(0);

  for (const row of reconciliation.rows) {
    if (row.currency !== group) return null;
    row.nets.forEach((net, column) => {
      if (column < totals.length) totals[column] += net;
    });
  }

  // `|| 0`: a sum that rounds to a negative zero would otherwise format as
  // "-0.00" and read as a debt.
  return totals.map(total => roundToCurrencyPrecision(total, group) || 0);
}
